import React, { useEffect, useState } from 'react';
import { CalendarClock, PartyPopper, Heart } from 'lucide-react';
import { AAWTA } from '@/lib/aawta';

const pad = (n) => String(n).padStart(2, '0');

const getState = () => {
  const now = Date.now();
  const start = new Date(AAWTA.summitStartIso).getTime();
  const end = new Date(AAWTA.summitEndIso).getTime();
  if (now < start) return { phase: 'before', diff: start - now };
  if (now >= start && now < end) return { phase: 'live' };
  return { phase: 'after' };
};

const breakdown = (ms) => {
  const total = Math.max(0, Math.floor(ms / 1000));
  return {
    days: Math.floor(total / 86400),
    hours: Math.floor((total % 86400) / 3600),
    minutes: Math.floor((total % 3600) / 60),
    seconds: total % 60,
  };
};

const Unit = ({ value, label }) => (
  <div className="flex flex-col items-center min-w-[68px] md:min-w-[88px]">
    <div className="w-full rounded-xl bg-white/10 backdrop-blur-sm border border-white/15 py-3 md:py-4 text-white font-display font-bold text-2xl md:text-4xl tabular-nums leading-none">
      {pad(value)}
    </div>
    <div className="mt-2 text-[10px] md:text-[11px] font-bold uppercase tracking-[0.18em] text-white/80">
      {label}
    </div>
  </div>
);

export const Countdown = () => {
  const [state, setState] = useState(getState);
  useEffect(() => {
    const id = setInterval(() => setState(getState()), 1000);
    return () => clearInterval(id);
  }, []);

  return (
    <section
      data-testid="summit-countdown"
      className="relative overflow-hidden bg-gradient-to-r from-aawta-navy-deep via-aawta-navy to-aawta-magenta"
    >
      <div
        className="absolute inset-0 opacity-30"
        style={{
          backgroundImage:
            'radial-gradient(circle at 20% 30%, rgba(255,255,255,0.15) 0, transparent 40%), radial-gradient(circle at 80% 60%, rgba(255,255,255,0.12) 0, transparent 45%)',
        }}
      />
      <div className="relative max-w-7xl mx-auto px-6 lg:px-10 py-8 md:py-12">
        {state.phase === 'before' && (() => {
          const t = breakdown(state.diff);
          return (
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-6">
              <div className="text-white">
                <div className="inline-flex items-center gap-2 text-[10.5px] font-bold uppercase tracking-[0.2em] text-white/80">
                  <CalendarClock className="h-3.5 w-3.5" /> Countdown to Summit
                </div>
                <h2 className="mt-2 font-display font-bold text-2xl md:text-3xl lg:text-4xl tracking-tight leading-tight">
                  The Workforce Summit Begins In
                </h2>
                <div className="mt-1.5 text-[13px] md:text-[14px] text-white/80">
                  Thursday, September 17, 2026 · 8:00 AM Eastern Time
                </div>
              </div>
              <div className="flex items-start gap-2.5 md:gap-4 justify-center md:justify-end">
                <Unit value={t.days} label="Days" />
                <div className="text-white/40 font-display text-2xl md:text-4xl leading-none pt-3 md:pt-4">:</div>
                <Unit value={t.hours} label="Hours" />
                <div className="text-white/40 font-display text-2xl md:text-4xl leading-none pt-3 md:pt-4">:</div>
                <Unit value={t.minutes} label="Minutes" />
                <div className="text-white/40 font-display text-2xl md:text-4xl leading-none pt-3 md:pt-4">:</div>
                <Unit value={t.seconds} label="Seconds" />
              </div>
            </div>
          );
        })()}

        {state.phase === 'live' && (
          <div className="flex items-center justify-center gap-4 text-white text-center py-4" data-testid="summit-live">
            <PartyPopper className="h-8 w-8 md:h-10 md:w-10 text-white shrink-0 animate-pulse" />
            <div>
              <div className="inline-flex items-center gap-2 text-[10.5px] font-bold uppercase tracking-[0.2em] text-white/80">
                <span className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse" /> Live Now · Sep 17, 2026
              </div>
              <div className="mt-1 font-display font-bold text-2xl md:text-4xl tracking-tight leading-tight">
                The Workforce Summit Is Happening Now!
              </div>
            </div>
          </div>
        )}

        {state.phase === 'after' && (
          <div className="flex items-center justify-center gap-4 text-white text-center py-4" data-testid="summit-thanks">
            <Heart className="h-7 w-7 md:h-9 md:w-9 text-white shrink-0" />
            <div>
              <div className="inline-flex items-center gap-2 text-[10.5px] font-bold uppercase tracking-[0.2em] text-white/80">
                <CalendarClock className="h-3.5 w-3.5" /> AAWTA · Workforce Collaborative
              </div>
              <div className="mt-1 font-display font-bold text-xl md:text-3xl tracking-tight leading-tight">
                Thank You for Being Part of the 2026 Workforce Summit!
              </div>
            </div>
          </div>
        )}
      </div>
    </section>
  );
};

export default Countdown;
